import { ERROR_CODES, type ErrorCode } from "./codes";
import { ApiError } from "./api-error";

/**
 * Translation function type (compatible with i18next's t function)
 */
type TranslateFunction = (key: string, options?: { defaultValue?: string }) => string;

/**
 * Maps Better Auth error codes to our error codes.
 * Better Auth uses snake_case codes like "INVALID_EMAIL_OR_PASSWORD".
 */
const BETTER_AUTH_CODE_MAP: Record<string, ErrorCode> = {
  // Login/password errors
  INVALID_EMAIL_OR_PASSWORD: ERROR_CODES.INVALID_CREDENTIALS,
  INVALID_PASSWORD: ERROR_CODES.INVALID_CREDENTIALS,
  USER_NOT_FOUND: ERROR_CODES.INVALID_CREDENTIALS,

  // Registration errors
  USER_ALREADY_EXISTS: ERROR_CODES.EMAIL_ALREADY_EXISTS,
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: ERROR_CODES.EMAIL_ALREADY_EXISTS,

  // Email errors
  INVALID_EMAIL: ERROR_CODES.INVALID_EMAIL,
  EMAIL_NOT_VERIFIED: ERROR_CODES.EMAIL_NOT_VERIFIED,

  // Password validation
  PASSWORD_TOO_SHORT: ERROR_CODES.PASSWORD_TOO_SHORT,
  PASSWORD_TOO_LONG: ERROR_CODES.PASSWORD_TOO_LONG,

  // Session errors
  SESSION_EXPIRED: ERROR_CODES.SESSION_EXPIRED,
  FAILED_TO_GET_SESSION: ERROR_CODES.UNAUTHORIZED,

  // Account errors
  ACCOUNT_NOT_FOUND: ERROR_CODES.ACCOUNT_NOT_FOUND,
  CREDENTIAL_ACCOUNT_NOT_FOUND: ERROR_CODES.INVALID_CREDENTIALS,

  // User creation errors
  FAILED_TO_CREATE_USER: ERROR_CODES.FAILED_TO_CREATE_USER,
  FAILED_TO_CREATE_SESSION: ERROR_CODES.FAILED_TO_CREATE_SESSION,
};

/**
 * Fallback patterns for common error messages.
 * Used when error doesn't have a code or the code is not translated.
 */
const ERROR_MESSAGE_PATTERNS: Array<{ pattern: RegExp; code: ErrorCode }> = [
  { pattern: /failed to fetch/i, code: ERROR_CODES.INTERNAL_ERROR },
  { pattern: /network error/i, code: ERROR_CODES.INTERNAL_ERROR },
  { pattern: /invalid email or password/i, code: ERROR_CODES.INVALID_CREDENTIALS },
  { pattern: /invalid credentials/i, code: ERROR_CODES.INVALID_CREDENTIALS },
  { pattern: /user not found/i, code: ERROR_CODES.INVALID_CREDENTIALS },
  { pattern: /already exists/i, code: ERROR_CODES.EMAIL_ALREADY_EXISTS },
  { pattern: /already registered/i, code: ERROR_CODES.EMAIL_ALREADY_EXISTS },
  { pattern: /email already/i, code: ERROR_CODES.EMAIL_ALREADY_EXISTS },
  { pattern: /invalid email/i, code: ERROR_CODES.INVALID_EMAIL },
  { pattern: /password too short/i, code: ERROR_CODES.PASSWORD_TOO_SHORT },
  { pattern: /password too long/i, code: ERROR_CODES.PASSWORD_TOO_LONG },
  { pattern: /session expired/i, code: ERROR_CODES.SESSION_EXPIRED },
];

/**
 * Get translated error message from an error object.
 *
 * @param error - The error object (can be ApiError, Error, or null)
 * @param t - The translation function from react-i18next
 * @param fallbackKey - The fallback translation key (default: "error.unknownError")
 * @returns The translated error message
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * const message = getErrorMessage(error, t);
 * ```
 */
export function getErrorMessage(
  error: Error | null | undefined,
  t: TranslateFunction,
  fallbackKey = "error.unknownError"
): string {
  if (!error) {
    return t(fallbackKey, { defaultValue: "An error occurred" });
  }

  // If it's an ApiError with a code, try to translate it
  if (error instanceof ApiError) {
    // First, check if it's a Better Auth error code and map it
    const mappedCode = BETTER_AUTH_CODE_MAP[error.code] || error.code;
    const translated = t(`error.codes.${mappedCode}`, { defaultValue: "" });
    if (translated) {
      return translated;
    }
  }

  // Try pattern matching on the error message
  const message = error.message.toLowerCase();
  for (const { pattern, code } of ERROR_MESSAGE_PATTERNS) {
    if (pattern.test(message)) {
      const translated = t(`error.codes.${code}`, { defaultValue: "" });
      if (translated) {
        return translated;
      }
    }
  }

  // Special handling for "Failed to fetch" (common network error)
  if (message.includes("failed to fetch")) {
    return t("error.failedToFetch", { defaultValue: "Failed to fetch data" });
  }

  // Return the original message if no translation found
  return error.message;
}

/**
 * Get translated auth error message.
 * Specialized for authentication-related errors.
 *
 * @param error - The error object
 * @param t - The translation function
 * @param isLogin - Whether this is a login error (vs registration)
 * @returns The translated error message
 */
export function getAuthErrorMessage(
  error: Error | null | undefined,
  t: TranslateFunction,
  isLogin: boolean
): string {
  if (!error) {
    return isLogin
      ? t("auth.loginFailed", { defaultValue: "Login failed" })
      : t("auth.registrationFailed", { defaultValue: "Registration failed" });
  }

  // If it's an ApiError with a code, try to translate it
  if (error instanceof ApiError) {
    // Map Better Auth error codes to our error codes
    const mappedCode = BETTER_AUTH_CODE_MAP[error.code] || error.code;

    // Try auth-specific translation first
    const authTranslated = t(`auth.errors.${mappedCode}`, { defaultValue: "" });
    if (authTranslated) {
      return authTranslated;
    }

    // Fall back to general error codes translation
    const generalTranslated = t(`error.codes.${mappedCode}`, { defaultValue: "" });
    if (generalTranslated) {
      return generalTranslated;
    }
  }

  // Try pattern matching on the message
  const message = error.message.toLowerCase();

  // Login errors
  if (isLogin) {
    if (
      message.includes("invalid email or password") ||
      message.includes("invalid credentials") ||
      message.includes("user not found")
    ) {
      return t("auth.invalidCredentials", {
        defaultValue: "Invalid email or password",
      });
    }
    if (message.includes("session expired")) {
      return t("auth.sessionExpired", {
        defaultValue: "Session expired. Please login again.",
      });
    }
    return t("auth.loginFailed", { defaultValue: "Login failed" });
  }

  // Registration errors
  if (
    message.includes("already exists") ||
    message.includes("already registered") ||
    message.includes("email already")
  ) {
    return t("auth.emailAlreadyExists", {
      defaultValue: "This email is already registered",
    });
  }
  if (message.includes("invalid email")) {
    return t("auth.invalidEmail", { defaultValue: "Invalid email format" });
  }
  if (message.includes("password too short")) {
    return t("auth.passwordTooShort", {
      defaultValue: "Password must be at least 8 characters",
    });
  }
  if (message.includes("password too long")) {
    return t("auth.passwordTooLong", {
      defaultValue: "Password is too long",
    });
  }

  return t("auth.registrationFailed", { defaultValue: "Registration failed" });
}
